import {
  AVATAR_CANONICAL_CONTENT_TYPE,
  AVATAR_CANONICAL_SIZE,
  AVATAR_MAX_UPLOAD_BYTES,
  avatarAcceptedMimeTypes,
} from '@shared/schemas';

const AVATAR_PROCESSING_QUALITY = 0.82;
const AVATAR_MAX_SOURCE_BYTES = 15 * 1024 * 1024;

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Impossible de générer le fichier avatar.'));
        return;
      }

      resolve(blob);
    }, type, quality);
  });
}

async function createImageBitmapWithOrientation(file: File) {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return createImageBitmap(file);
  }
}

function buildAvatarFilename(originalFilename: string) {
  const stem = originalFilename
    .replace(/\.[^.]+$/, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');

  return `${stem || 'avatar'}.webp`;
}

export function getAvatarAcceptedFileTypes() {
  return avatarAcceptedMimeTypes.join(',');
}

export type ProcessedAvatarUpload = {
  file: File;
  previewUrl: string;
};

export async function processAvatarUpload(file: File): Promise<ProcessedAvatarUpload> {
  if (!avatarAcceptedMimeTypes.includes(file.type as (typeof avatarAcceptedMimeTypes)[number])) {
    throw new Error('Formats supportés : JPEG, PNG ou WebP.');
  }

  if (file.size > AVATAR_MAX_SOURCE_BYTES) {
    throw new Error('Le fichier source est trop volumineux pour être traité.');
  }

  const imageBitmap = await createImageBitmapWithOrientation(file);

  try {
    const sourceSize = Math.min(imageBitmap.width, imageBitmap.height);
    const sourceX = (imageBitmap.width - sourceSize) / 2;
    const sourceY = (imageBitmap.height - sourceSize) / 2;

    const canvas = document.createElement('canvas');
    canvas.width = AVATAR_CANONICAL_SIZE;
    canvas.height = AVATAR_CANONICAL_SIZE;

    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('Le traitement image n est pas disponible dans ce navigateur.');
    }

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(
      imageBitmap,
      sourceX,
      sourceY,
      sourceSize,
      sourceSize,
      0,
      0,
      AVATAR_CANONICAL_SIZE,
      AVATAR_CANONICAL_SIZE,
    );

    const blob = await canvasToBlob(
      canvas,
      AVATAR_CANONICAL_CONTENT_TYPE,
      AVATAR_PROCESSING_QUALITY,
    );

    if (blob.size > AVATAR_MAX_UPLOAD_BYTES) {
      throw new Error('Le fichier avatar compressé reste trop volumineux.');
    }

    return {
      file: new File([blob], buildAvatarFilename(file.name), {
        lastModified: Date.now(),
        type: AVATAR_CANONICAL_CONTENT_TYPE,
      }),
      previewUrl: URL.createObjectURL(blob),
    };
  } finally {
    imageBitmap.close();
  }
}
